<template>
  <InitializePage>
    <Page
      :menus="menus"
      :menuTheme="menuTheme"
      v-model:selectedKey="activeMenu"
      v-model:expandedKeys="expandedKeys"
      title="容器服务"
      @select="onMenuItemSelected"
    >
      <router-view />
    </Page>
  </InitializePage>
</template>

<script lang="tsx">
import { Page } from "kdc-components-vue";
import { InitializePage } from "./components/InitializePage";
import {
  type BaseInfo,
  baseInfo,
  BaseInfoContextKey,
  checkProductPermission,
  registerBaseInfoChanged,
} from "@kce2/common/core/baseinfo";
import { appendCommonQuery, registryRouterGuard } from "./locationUtils";
import { useRoute, useRouter, type RouteLocation } from "vue-router";
import { computed, defineComponent, onErrorCaptured, onMounted, provide, ref, watch } from "vue";
import { useDispose } from "kdc-hooks";
import "@morev/vue-transitions/styles";
import "kdc-components-vue/style.css";
import { useRegionInit } from "@kce2/common/core/hooks/use-region-init";
import { useMenu } from "./hooks/use-menu";
import { useConsoleTheme } from "./hooks/use-console-theme";

export default defineComponent({
  name: "MainApp",
  components: {
    Page,
    InitializePage,
  },
  setup() {
    const toDispose = useDispose();
    provide(BaseInfoContextKey, baseInfo);
    const activeMenu = ref<string | number | undefined>(undefined);
    const expandedKeys = ref<string[]>([]);
    // const serviceIsOpened = baseInfo.serviceIsOpened;

    onErrorCaptured(e => {
      console.error("Unhandled error", e);
    });

    const router = useRouter();
    const route = useRoute();
    // ...mapMutations(["setRegion", "setNamespace", "setRegions", "setNamespaces"]),
    function onMenuItemSelected(key: string | Event) {
      // console.debug("onMenuItemSelected", key);
      if (!key || key instanceof Event) {
        return;
      }
      const [primaryMenu, secondaryMenu] = key.split?.("-") || [];
      if (primaryMenu) {
        gotoMenuItem(primaryMenu, secondaryMenu ?? "");
      }
    }
    function gotoMenuItem(primaryMenu: string, secondaryMenu: string) {
      router.push({
        path: `/${primaryMenu}/${secondaryMenu || ""}`,
      });
    }
    function onCommonInfoChanged(val: BaseInfo) {
      appendCommonQuery(
        {
          region: val.region,
        },
        route,
      );
    }

    const region = computed(() => {
      return baseInfo.region;
    });

    onMounted(() => {
      // Watch region BEFORE init so checkProductPermission fires when region is set
      watch(
        () => baseInfo.region,
        () => {
          console.debug("baseInfo.region", baseInfo.region);
          checkProductPermission();
        },
      );

      const { init } = useRegionInit();

      router.isReady().then(async () => {
        // Initialize region via hook
        await init();

        const serviceIsOpened = baseInfo.serviceIsOpened;

        if (serviceIsOpened) {
          onCommonInfoChanged(baseInfo);
        }

        console.debug("baseInfo", baseInfo);

        toDispose.append(
          registerBaseInfoChanged(v => {
            onCommonInfoChanged(v);
          }),
        );

        registryRouterGuard(router);
      });
    });

    const { menus } = useMenu({ region: () => baseInfo.region });

    // 菜单栏跟随金山云全局导航的 light/dark 主题
    const { theme: menuTheme } = useConsoleTheme();

    return {
      activeMenu,
      expandedKeys,
      menus,
      menuTheme,
      region,
      onMenuItemSelected,
    };
  },
  watch: {
    $route(to: RouteLocation) {
      const { primaryMenu, secondaryMenu }: { primaryMenu?: string; secondaryMenu?: string } =
        to.meta || {};

      if (primaryMenu) {
        if (secondaryMenu) {
          this.activeMenu = `${primaryMenu}-${secondaryMenu}`;
          this.expandedKeys = [primaryMenu];
        } else {
          this.activeMenu = primaryMenu;
        }
      }
    },
  },
});
</script>

<style lang="scss" src="./global.scss"></style>
